import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaHome, FaUser, FaGraduationCap, FaTicketAlt, FaCogs,
  FaProjectDiagram, FaChartPie, FaClipboardCheck, FaUsers,
  FaCalendar, FaBars, FaTimes, FaQrcode, FaPowerOff, FaBell
} from 'react-icons/fa';
import UserAvatar from './UserAvatar';
import BrandTicker from './BrandTicker';
import useRolePermissions from '../hooks/useRolePermissions';
import { serverLogout } from '../utils/api';

const API_ROLE = 'https://dev.sliitleo.org/Backend/user_role.php';

const menuItemsConfig = [
  { icon: <FaHome/>,             label: 'Home',             route: '/home',               exact: true },
  { icon: <FaUser/>,             label: 'My Profile',       route: '/profile',            exact: true },
  { icon: <FaGraduationCap/>,    label: 'Leo Education',    route: '/education',          exact: true },
  { icon: <FaTicketAlt/>,        label: 'My Tickets',       route: '/tickets',            exact: true },
  { icon: <FaCalendar/>,         label: 'Club Calendar',    route: '/club-calander',      exact: true },
  { separator: true },
  { icon: <FaCogs/>,             label: 'Admin Console',    route: '/admin',              exact: false },
  { icon: <FaProjectDiagram/>,   label: 'Club Projects',    route: '/club-projects',      exact: true },
  { icon: <FaChartPie/>,         label: 'Club Dashboard',   route: '/club-dashboard',     exact: true },
  { icon: <FaClipboardCheck/>,   label: 'Reporting Hub',    route: '/reporting-hub',      exact: true },
  { icon: <FaUsers/>,            label: 'Installation',     route: '/installation',       badge: null, exact: true },
  { icon: <FaGraduationCap/>,    label: 'Education Admin',  route: '/education-statistics', exact: true },
  { icon: <FaQrcode/>,           label: 'Attendance',       route: '/attendance',         exact: true },
];

// helpers
const readStoredUser = () => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } };
const normalizeRole = (role) =>
  String(role || '')
    .toLowerCase()
    .replace(/[.\-_/]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

// mid-tier roles list (unchanged)
const MID_ROLES = new Set([
  'secretary',
  'treasurer',
  'co asst treasurer',
  'co asst secretary',
  'co assistant treasurer',
  'installation sub committee member',
  'co assistant secretary',
]);

const MID_ROLE_ALLOWED_LABELS = new Set([
  'Home',
  'My Profile',
  'Leo Education',
  'My Tickets',
  'Club Calendar',
  'Reporting Hub',
  'Club Projects',
  'Installation',
]);

// NEW: restricted roles list you gave — show member-only by default
const RESTRICTED_ROLES = new Set([
  'chief editor',
  'president program coordinator',
  'club performence evaluator',      // keep given spelling
  'club performance evaluator',      // and a safer variant just in case
  'club director',
  'team head',
  'activity director',
  'past president',
  'past board member',
  'it department',
  'installation sub committee member',  // normalized “sub-committee”
  'reporting sub committee',
  'committee member',
]);

// NEW: roles that also see Installation
const SEE_INSTALLATION_ROLES = new Set([
  'installation sub committee member',
  'president program coordinator',
  'club performence evaluator',
  'club performance evaluator',
  'secretary',
  'treasurer',
]);

// NEW: roles that also see Reporting Hub
const SEE_REPORTING_HUB_ROLES = new Set([
  'club performence evaluator',
  'club performance evaluator',
  'reporting sub committee',
]);

const baseMemberMenu = () => {
  const cut = menuItemsConfig.findIndex(i => i.separator);
  return cut === -1 ? [...menuItemsConfig] : menuItemsConfig.slice(0, cut);
};

const findItem = (label) => menuItemsConfig.find(i => i.label === label);

// True admins only (System Admin / President) -- mirrors ADMIN_ROLES in
// src/config/roles.js and Backend/birthday_roles.php's
// ADMIN_ROLES_FOR_BIRTHDAY_PUSH exactly, so "only admins see this" means
// the same set of people everywhere in the app, not just "leaders" (which
// the "everyone else" branch below is much broader than).
const ADMIN_NAV_ROLES = new Set(['system admin', 'president', 'club president']);
const NOTIFICATION_SETTINGS_ITEM = {
  icon: <FaBell/>, label: 'Notification Settings', route: '/notification-settings', exact: true,
};

// main builder
const buildMenuByRole = (role) => {
  const norm = normalizeRole(role);
  const isAdmin = ADMIN_NAV_ROLES.has(norm);

  // Member only
  if (norm === 'club member' || norm === 'member') {
    return baseMemberMenu();
  }

  // Your “restricted list”: member-only + specific extras
  if (RESTRICTED_ROLES.has(norm)) {
    const list = baseMemberMenu();

    if (SEE_INSTALLATION_ROLES.has(norm)) {
      const installation = findItem('Installation');
      if (installation) list.push(installation);
    }
    if (SEE_REPORTING_HUB_ROLES.has(norm)) {
      const reportHub = findItem('Reporting Hub');
      if (reportHub) list.push(reportHub);
    }
    if (isAdmin) list.push(NOTIFICATION_SETTINGS_ITEM);
    return list;
  }

  // Mid-tier (secretariat/finance)
  if (MID_ROLES.has(norm)) {
    const list = menuItemsConfig.filter(
      item => !item.separator && MID_ROLE_ALLOWED_LABELS.has(item.label)
    );
    if (isAdmin) list.push(NOTIFICATION_SETTINGS_ITEM);
    return list;
  }

  // Everyone else (leaders/admins) gets full menu, plus Notification
  // Settings only for the narrower true-admin set above.
  return isAdmin ? [...menuItemsConfig, NOTIFICATION_SETTINGS_ITEM] : menuItemsConfig;
};

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [menuItems, setMenuItems] = useState(buildMenuByRole('Club Member'));
  const [role, setRole] = useState('Club Member');
  const [profile, setProfile] = useState(() => readStoredUser());

  const [isOpen, setIsOpen] = useState(() => window.innerWidth >= 1024);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [expandedItems, setExpandedItems] = useState({});

  // fetch role
  useEffect(() => {
    const stored = readStoredUser();
    setProfile(stored);
    const payload =
      stored.SLIITLeoID ? { id: stored.SLIITLeoID } :
      (stored.email || stored.Email) ? { email: stored.email || stored.Email } :
      null;

    if (!payload) {
      const r = stored.role || 'Club Member';
      setRole(r);
      setMenuItems(buildMenuByRole(r));
      return;
    }

    (async () => {
      try {
        const res = await fetch(API_ROLE, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        const r = (data?.success && (data.user?.role || 'Club Member')) || (stored.role || 'Club Member');
        setRole(r);
        setMenuItems(buildMenuByRole(r));
        localStorage.setItem('user', JSON.stringify({ ...stored, role: r }));
      } catch {
        const r = stored.role || 'Club Member';
        setRole(r);
        setMenuItems(buildMenuByRole(r));
      }
    })();
  }, []);

  // Optional nav override, configured per-role on the Role & Path Management
  // page. Only takes effect once an admin has explicitly saved at least one
  // ROUTE-type path for this exact role name (permission-type rows, like
  // Installation's Verify/Check-in button keys, never affect nav -- see
  // useRolePermissions.js) -- otherwise `allowedRoutes` stays null and
  // every existing role keeps its current hardcoded menu, unchanged.
  const rolePerm = useRolePermissions(role);
  const allowedRoutes = rolePerm.hasSavedRouteRows ? rolePerm.savedRoutes : null;

  const menuItemsWithState = useMemo(
    () =>
      menuItems
        // Notification Settings is gated purely by the ADMIN_NAV_ROLES check
        // in buildMenuByRole() above, not by the configurable role_paths
        // list (it isn't one of the 12 rows in the `paths` table), so it
        // must never be caught by this filter once it's already been added.
        .filter((item) => !allowedRoutes || item.separator || item.route === NOTIFICATION_SETTINGS_ITEM.route || allowedRoutes.has(item.route))
        .map((item) => ({
          ...item,
          isActive:
            item.route &&
            (item.exact ? location.pathname === item.route : location.pathname.startsWith(item.route)),
        })),
    [menuItems, location.pathname, allowedRoutes]
  );

  // responsive open/close
  useEffect(() => {
    let t;
    const onResize = () => {
      clearTimeout(t);
      t = setTimeout(() => {
        const mobile = window.innerWidth < 1024;
        setIsMobile(mobile);
        setIsOpen(!mobile);
      }, 100);
    };
    window.addEventListener('resize', onResize);
    onResize();
    return () => {
      window.removeEventListener('resize', onResize);
      clearTimeout(t);
    };
  }, []);

  // installation badge (if present in current menu)
  useEffect(() => {
    const run = async () => {
      try {
        const res = await fetch('https://dev.sliitleo.org/Backend/installation_total.php');
        if (!res.ok) throw new Error('Bad network');
        const data = await res.json();
        if (typeof data.total === 'number') {
          setMenuItems((items) =>
            items.map((it) => (it.label === 'Installation' ? { ...it, badge: data.total } : it))
          );
        }
      } catch (e) {
        console.error('Failed to fetch installation total:', e);
      }
    };
    run();
  }, []);

  useEffect(() => {
    if (!isMobile || !isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setIsOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isMobile, isOpen]);

  const toggleMenu   = useCallback(() => setIsOpen((p) => !p), []);
  const toggleExpand = useCallback((label) => setExpandedItems((prev) => ({ ...prev, [label]: !prev[label] })), []);
  const handleLogout = useCallback(() => {
    serverLogout();
    try { localStorage.clear(); sessionStorage.clear(); } catch {}
    navigate('/login');
    if (isMobile) setIsOpen(false);
  }, [navigate, isMobile]);

  return (
    <>
      {isMobile && !isOpen && (
        <button onClick={toggleMenu} className="sb-burger" aria-label="Open menu">
          <FaBars size={18} />
        </button>
      )}

      <AnimatePresence>
        {isMobile && isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="sb-scrim"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />
        )}
      </AnimatePresence>

      <motion.aside
        initial={{ x: isMobile ? -320 : 0 }}
        animate={{
          x: isOpen ? 0 : -320,
          opacity: 1,
          display: isMobile ? 'block' : 'flex',
        }}
        transition={{ type: 'spring', stiffness: 520, damping: 38 }}
        className="sidebar fixed top-0 left-0 h-screen w-64 z-40 flex flex-col p-3"
        style={isMobile ? { width: 'min(80vw, 260px)' } : undefined}
        aria-label="Main navigation"
        drag={isMobile ? 'x' : false}
        dragDirectionLock
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={{ left: 0.5, right: 0 }}
        dragSnapToOrigin
        onDragEnd={(_, info) => {
          if (info.offset.x < -70 || info.velocity.x < -450) setIsOpen(false);
        }}
      >
        {isMobile && (
          <button onClick={() => setIsOpen(false)} className="sb-close" aria-label="Close menu">
            <FaTimes size={14} />
          </button>
        )}

        <div className="org">
          <BrandTicker size={30} />
        </div>

        <nav className="sb-nav flex-1 overflow-y-auto" style={{ overflowX: 'hidden' }}>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }} className="nav-group">
            {menuItemsWithState.map((item, index) => {
              if (item.separator) {
                return (
                  <li key={`sep-${index}`} className="nav-label" role="presentation">
                    Administration
                  </li>
                );
              }
              const isActive = item.isActive;
              const hasChildren = item.children && item.children.length > 0;
              const isExpanded = !!expandedItems[item.label];

              return (
                <li key={item.label} style={{ listStyle: 'none' }}>
                  <motion.div
                    whileTap={{ scale: 0.98 }}
                    className={`nav-item${isActive ? ' active' : ''}`}
                    onClick={() => {
                      if (hasChildren) {
                        toggleExpand(item.label);
                      } else {
                        navigate(item.route);
                        if (isMobile) setIsOpen(false);
                      }
                    }}
                  >
                    {item.icon}
                    <span style={{ flex: 1 }}>{item.label}</span>

                    {item.badge !== null && item.badge !== undefined && (
                      <span className="nav-badge">{item.badge}</span>
                    )}
                    {hasChildren && (
                      <motion.span
                        animate={{ rotate: isExpanded ? 0 : -90 }}
                        transition={{ duration: 0.2 }}
                        style={{ color: 'var(--sidebar-text-3)', display: 'grid' }}
                      >
                        <svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.08 1.04l-4.25 4.25a.75.75 0 01-1.06 0L5.21 8.27a.75.75 0 01.02-1.06z"/>
                        </svg>
                      </motion.span>
                    )}
                  </motion.div>

                  <AnimatePresence>
                    {hasChildren && isExpanded && (
                      <motion.ul
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        style={{ marginLeft: 30, marginTop: 4, overflow: 'hidden', listStyle: 'none', padding: 0 }}
                      >
                        {item.children.map((child) => {
                          const isChildActive = location.pathname === child.route;
                          return (
                            <motion.li
                              key={child.label}
                              whileHover={{ x: 3 }}
                              className="nav-item"
                              style={{
                                fontSize: 12.5,
                                paddingTop: 6,
                                paddingBottom: 6,
                                color: isChildActive ? 'var(--accent)' : undefined,
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(child.route);
                                if (isMobile) setIsOpen(false);
                              }}
                            >
                              {child.label}
                            </motion.li>
                          );
                        })}
                      </motion.ul>
                    )}
                  </AnimatePresence>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="sidebar-foot">
          <button
            onClick={() => { navigate('/profile'); if (isMobile) setIsOpen(false); }}
            className="profile-mini"
            title="My Profile"
          >
            <span className="profile-avatar">
              <UserAvatar src={profile?.image} size={30} verified ring="var(--sidebar-bg)" />
            </span>
            <span style={{ minWidth: 0, flex: 1 }}>
              <span className="profile-name">{profile?.name || 'Leo Member'}</span>
              <span className="profile-role">{role}</span>
            </span>
          </button>

          <button onClick={handleLogout} className="nav-item nav-logout" title="Logout">
            <FaPowerOff />
            <span>Logout</span>
          </button>

          <div className="sb-powered">
            Powered by <b>SLIIT Leos</b>
          </div>
        </div>
      </motion.aside>
    </>
  );
};

export default React.memo(Sidebar);
